// cSpell:enableCompoundWords
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GameWeek, NFL_GAMES_PER_SEASON, Season, TeamAbrvToTeamID } from '@shared/constants';
import {
  breakSeasonWeekID,
  getDateInMSFromEpoch,
  getGameDateByGameID,
  getSeasonIdFromGameID,
  getSeasonWeekIDByGameID,
  getTeamIDsFromGameID,
  isSeasonYearMatchUID,
  toNumber,
  toUID,
} from '@shared/helpers';
import { BYE, GameID, IEnhancedTeamStats, IGame, IGameStats, IMatchup, ITeamsOnByeWeek, UID } from '@shared/types';
import { saveMatchResults, saveMatchSchedule } from 'api/ScheduleAPI';
import { saveTeamMatchResults } from 'api/TeamsAPI';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { ArmyGreenCls, Cls, Emojis, ValueCls } from 'app/constants';
import { fetchGameStats, fetchWeeklySchedule } from 'app/fetchapi/FetchGames';
import jsonMatchupSchedule from 'app/fetchapi/schedule2024.json';
import {
  getShouldFetchGameSchedule,
  getShouldFetchGameStats,
  getShouldFetchTeamGameStats,
} from 'app/slices/SyncManagerSlice';
import { UserAuthState } from 'app/slices/UserSlice';
import { sortTeamMatchupsByGameID, sortWeeklyGamesByDateTime } from 'helpers/SortHelpers';
import { generateTeamStatsPayload, getTeamStatsFromGameStats } from 'helpers/StatsHelper';
import { getIndexDBLoaderWebWorker, getLoadStoreRequestMessage } from 'helpers/StoreHelpers';
import { getNflSeasonTeamStats } from 'helpers/TeamHelpers';
import { updateRecordsInIDBDatabase } from 'lib/idb/IndexDbInterface';
import {
  AppThunk,
  GameResultsPayload,
  GameScheduleByYearPayload,
  GameSchedulePayload,
  IWorkerIDBLoaderResponseMessage,
  MatchupsSchedulePayload,
  Maybe,
  PredictionsForMatchupsPayload,
  RootState,
  SeasonalGameIDMap,
  SeasonID,
  SeasonWeekID,
  Stores,
  TeamResultPayload,
} from 'types';
import { getSelectedGame, getSelectedWeek, getSelectedYear } from './GamesSlice';
import { doUpdateTeamSchedule, syncLeagueReduxWithIDB } from './LeagueSlice';

// #region REDUX INIT *********************************************************
export type LoadedScheduleByYear = {
  [K in Season]: boolean;
};

export type StoresLoadedState = {
  scheduleLoaded: boolean;
  matchupsLoaded: boolean;
  gameResultsLoaded: boolean;
  teamResultsLoaded: boolean;
  loadedSchedule: LoadedScheduleByYear;
};

export type ScheduleByYear = {
  [K in Season]: (IGame | IMatchup)[];
};

export type GameScheduleProps = {
  games: (IGame | IMatchup)[];
  matchups: IMatchup[];
  gameResults: IGameStats[];
  teamResults: IEnhancedTeamStats[];
  scheduleByYear: ScheduleByYear;
  isLoading: boolean;
  reloadScheduleFlag: boolean;
  storesLoadedState: StoresLoadedState;
  gamesFetchingQueue: GameID[];
};

const generateInitialScheduleByYear = (): ScheduleByYear => {
  const initialSchedule = {} as ScheduleByYear;
  (Object.keys(Season) as Array<keyof typeof Season>).forEach((seasonKey) => {
    const seasonValue = Season[seasonKey];
    initialSchedule[seasonValue] = [];
  });
  return initialSchedule;
};

const generateInitialLoadedSchedule = (): LoadedScheduleByYear => {
  const initialLoaded = {} as LoadedScheduleByYear;
  (Object.keys(Season) as Array<keyof typeof Season>).forEach((seasonKey) => {
    const seasonValue = Season[seasonKey];
    initialLoaded[seasonValue] = false;
  });
  return initialLoaded;
};

// const byeWeeksInfoInitialState: TeamsOnByeWeek = getTeamsOnByeWeekInitialState();

const initialState: GameScheduleProps = {
  games: [],
  matchups: [],
  gameResults: [],
  teamResults: [],
  scheduleByYear: generateInitialScheduleByYear(),
  isLoading: false,
  reloadScheduleFlag: false,
  storesLoadedState: {
    scheduleLoaded: false,
    matchupsLoaded: false,
    gameResultsLoaded: false,
    teamResultsLoaded: false,
    loadedSchedule: generateInitialLoadedSchedule(),
  },
  gamesFetchingQueue: [],
};
// #endregion

const CONSOLE_ON = getConsoleState(ConsoleID.GameScheduleSlice);

// #region THUNK ACTIONS should be deprected as they are moved to CLFunctions
export type WeeklyScheduleFetchedCb = (weeklyGames: string, weekNum: number) => void;

export const populateLeagueMatchupSchedule = (): AppThunk => (dispatch) => {
  const teams: IMatchup[] = jsonMatchupSchedule.schedule2024 as IMatchup[];
  const sortedMatchups = teams.sort(sortTeamMatchupsByGameID);
  dispatch(setMatchupsSchedule(sortedMatchups));
  saveMatchSchedule(sortedMatchups, '2024');
};

export const populateMatchResultsInFB = (): AppThunk => (dispatch, getState) => {
  const matchResults: IGameStats[] = getState().schedule.gameResults;
  const year = getState().nfl.yearSelected;

  saveMatchResults(matchResults, year);
};

export const populateLeagueMatchupBoxScore =
  (year: SeasonID = '2024'): AppThunk =>
  (dispatch, getState) => {
    const teams: IMatchup[] = getState().schedule.matchups;
    const boxScores: IGameStats[] = getState().schedule.gameResults;
    const teamResults: IEnhancedTeamStats[] = getState().schedule.teamResults;

    const gameIDs: GameID[] = teams.map((team) => team.gameID);

    const gameIDsCount = gameIDs.length;
    const boxScoresCount = boxScores.length;

    if (boxScoresCount < gameIDsCount) {
      dispatch(doFetchGameStats(gameIDs[boxScoresCount]));
    } else {
      saveTeamMatchResults(teamResults, year);
    }

    // const byeWeekTeams: ITeamsOnByeWeek[] = getTeamsOnByeWeek(teams);
    // if (CONSOLE_ON) console.log('++++++++++++++++++++++++++++ byeWeekTeams', byeWeekTeams);
    // dispatch(setByeWeeks(byeWeekTeams));
  };

export const doFetchWeeklyScheduledGames =
  (weekNum = 1): AppThunk =>
  (dispatch) => {
    // TODO: @Andrey check if games are stored in the IDB Database

    // TODO: @Andrey if they are, load them from the IDB

    // if they are not in the IDB, fetch them from the API
    dispatch(setStoresLoadedState({ scheduleLoaded: false }));
    dispatch(setIsLoading(true));

    const callbackFn = (weeklyGames: string, weekNum: number) =>
      dispatch(processFetchedWeeklyScheduledGames(weeklyGames, weekNum));

    fetchWeeklySchedule(weekNum, 'reg', 2024, callbackFn);
  };

export const processFetchedWeeklyScheduledGames =
  (weeklyGames: string, weekNum: number): AppThunk =>
  (dispatch) => {
    if (CONSOLE_ON) console.log(`Week #${weekNum} / Games: ${weeklyGames}`);
    const games: IGame[] = JSON.parse(weeklyGames).body as IGame[];
    // const weekID = `Week${weekNum}` as GameWeek;

    dispatch(addNFLScheduleToInboxDB(games));
  };

export const doFetchGameStats =
  (gameID: string): AppThunk =>
  (dispatch, getState) => {
    if (CONSOLE_ON) console.log(`${Emojis.eyes} Fetching game stats for ${gameID}`);
    const getTime = getGameDateByGameID(gameID).getTime();
    const currentTime = Date.now();

    if (getTime > currentTime) {
      if (CONSOLE_ON) console.log(`❌ Game ${gameID} has not started yet`);
      return;
    }

    if (!isFetchingGameID(getState(), gameID) && !hasGameStatsByGameID(getState(), gameID)) {
      dispatch(addGameIDToQueue(gameID));
      dispatch(setStoresLoadedState({ gameResultsLoaded: false }));
      dispatch(setIsLoading(true));
      fetchGameStats(gameID);
    } else {
      const isFetching = isFetchingGameID(getState(), gameID);
      const msg = isFetching ? 'is already in the queue' : 'has been fetched';
      if (CONSOLE_ON) console.log(`${Emojis.redBrick} %cGame ${gameID}%c ${msg}`, ArmyGreenCls, '');
    }
  };

export const processFetchedGameStats =
  (gameStats: string, gameID: string): AppThunk =>
  (dispatch) => {
    if (CONSOLE_ON) console.log(`Received stats for ${gameID}`);
    const stats = JSON.parse(gameStats).body as IGameStats;
    // hiding the player stats for now
    stats.playerStats = {};

    dispatch(addNFLGameStatsToInboxDB([{ ...stats }]));
    dispatch(removeGameIDFromQueue(gameID));
    // Do TeamResults now
    dispatch(createTeamResultsFromGameResults(stats));

    dispatch(setIsLoading(false));
  };

export const createTeamResultsFromGameResults =
  (gameStats: IGameStats): AppThunk =>
  (dispatch) => {
    const teamResults: IEnhancedTeamStats[] = getTeamStatsFromGameStats(gameStats);
    dispatch(addNFLTeamStatsToInboxDB(teamResults));
  };

export const addTeamOnByeToSeasonTeamStats =
  (weekID: GameWeek): AppThunk =>
  (dispatch, getState) => {
    const byeWeek: Maybe<ITeamsOnByeWeek> = getState().league.byeWeekTeams.find((team) => team.weekID === weekID);
    if (byeWeek) {
      const byeWeekTeamStats: IEnhancedTeamStats[] = byeWeek.teamIDs.map((tID: string) => {
        const baseSeasonStats = getNflSeasonTeamStats(tID, weekID, BYE);
        return {
          ...baseSeasonStats,
          uID: toUID(weekID, tID, BYE),
        };
      });
      dispatch(addNFLTeamStatsToInboxDB(byeWeekTeamStats));
    }
  };

export const updateTeamStats =
  (gameStats: IGameStats): AppThunk =>
  (dispatch) => {
    const homePayload: IEnhancedTeamStats = generateTeamStatsPayload(gameStats, true);
    const awayPayload: IEnhancedTeamStats = generateTeamStatsPayload(gameStats, false);

    dispatch(addTeamStats([homePayload, awayPayload]));
  };

export const generateEachTeamScheduleByWeekID =
  (weekID: GameWeek): AppThunk =>
  (dispatch, getState) => {
    const games: (IGame | IMatchup)[] = getState().schedule.games;
    const weekGames = games.filter((game) => game.gameWeek.replace(' ', '') === weekID);
    const weekHasByeWeeks = weekGames.length > 0 && weekGames.length < 16;
    const teamIDs: string[] = Object.values(TeamAbrvToTeamID) as string[];
    if (CONSOLE_ON)
      console.log(`generateEachTeamScheduleByWeekID ${weekGames.length * 2} teams are playing in ${weekID}`);
    weekGames.forEach((game) => {
      const homeUID: UID = toUID(weekID, game.teamIDHome, game.gameID);
      const awayUID: UID = toUID(weekID, game.teamIDAway, game.gameID);

      teamIDs[parseInt(game.teamIDHome) - 1] = '0';
      teamIDs[parseInt(game.teamIDAway) - 1] = '0';

      dispatch(doUpdateTeamSchedule(homeUID, game.teamIDAway));
      dispatch(doUpdateTeamSchedule(awayUID, game.teamIDHome));
    });

    if (weekHasByeWeeks) {
      const byeWeekIDs = teamIDs.filter((teamID) => teamID !== '0');
      const msgOnByeWeek = `%c ${byeWeekIDs.length} %c teams are on a bye`;
      const msg = `generateEachTeamScheduleByWeekID ${msgOnByeWeek} during %c ${weekID} %c`;
      if (CONSOLE_ON) console.log(`${msg} `, Cls.Cnt, '', Cls.Val, '');
      byeWeekIDs.forEach((teamID) => {
        const uID: UID = toUID(weekID, teamID, BYE, getSeasonIdFromGameID(weekGames[0].gameID));
        dispatch(doUpdateTeamSchedule(uID, teamID));
      });
    }

    // TODO: Update the IDB database with the updated team schedules
    dispatch(syncLeagueReduxWithIDB());
  };
// #endregion

// #region SLICE DEFINITION ****************************************************
export const initGameScheduleStore = (): AppThunk => (dispatch, getState) => {
  dispatch(setIsLoading(true));

  const isScheduleEmpty = isGameScheduleEmpty(getState());
  const gameResultsEmpty = isGameResultsEmpty(getState());
  const teamResultsEmpty = isTeamResultsEmpty(getState());
  // const byeWeekTeamsEmpty = isByeWeekTeamsEmpty(getState());

  if (CONSOLE_ON) console.log(`{Store} Schedule     loaded: ${!isScheduleEmpty ? '✔️' : '❌'}`);
  if (CONSOLE_ON) console.log(`{Store} GameResults  loaded: ${!gameResultsEmpty ? '✔️' : '❌'}`);
  if (CONSOLE_ON) console.log(`{Store} TeamResults  loaded: ${!teamResultsEmpty ? '✔️' : '❌'}`);
  // if (CONSOLE_ON) console.log(`{Store} ByeWeekTeams loaded: ${!byeWeekTeamsEmpty ? '✔️' : '❌'}`);
};
// #endregion

// #region THUNKS CALLED FROM LANDING PAGE TO FETCH DATA FROM INDEX DB
/** @deprecated */
export const updateReduxWithGameSchedule =
  (games: IMatchup[]): AppThunk =>
  (dispatch) => {
    dispatch(setMatchupsSchedule(games));
    dispatch(setStoresLoadedState({ scheduleLoaded: true }));
  };
/** @deprecated */
export const updateReduxWithGameStats =
  (gameStats: IGameStats[]): AppThunk =>
  (dispatch) => {
    dispatch(setGameResults(gameStats));
    dispatch(setStoresLoadedState({ gameResultsLoaded: true }));
  };
/** @deprecated */
export const updateReduxWithTeamGameStats =
  (teamStats: IEnhancedTeamStats[]): AppThunk =>
  (dispatch) => {
    dispatch(setTeamStats(teamStats));
    dispatch(setStoresLoadedState({ teamResultsLoaded: true }));
  };

// #endregion

// #region LOADERS FROM IDB WEBWORKERS ****************************************
export const parseNFLSelectedWeekSchedule = (
  scheduleByYear: (IGame | IMatchup)[],
  weekID: GameWeek,
): (IGame | IMatchup)[] => {
  const selectedWeekStr = `Week ${toNumber(weekID)}`;
  return scheduleByYear.filter((game) => game.gameWeek === selectedWeekStr);
};

export const updateNFLSelectedWeekSchedule =
  (newSeasonID: SeasonID, newWeekID: GameWeek): AppThunk =>
  (dispatch, getState) => {
    if (getState().schedule.scheduleByYear[Season[`Season${newSeasonID}` as keyof typeof Season]].length === 0) {
      const msg = `updateNFLSelectedWeekSchedule %c empty schedule %c for %c ${newSeasonID} %c`;
      if (CONSOLE_ON) console.log(msg, Cls.Cri, '', Cls.Val, '');
      dispatch(setStoresLoadedState({ scheduleLoaded: false }));
      return;
    }
    const nflState = getState().nfl;
    const selectedYear = nflState.yearSelected;
    const selectedWeekID: GameWeek = nflState.weekSelected || GameWeek.Week1;
    const scheduleByYear = getState().schedule.scheduleByYear[Season[`Season${newSeasonID}` as keyof typeof Season]];
    const selectedWeekGames = parseNFLSelectedWeekSchedule(scheduleByYear, newWeekID);

    const msg = `%c updateNFLSelectedWeekSchedule %c %c ${selectedYear} %c %c ${selectedWeekID} %c`;
    if (CONSOLE_ON) console.log(msg, Cls.Loc, '', Cls.Val, '', Cls.DrkGn, '');
    dispatch(setGamesSchedule(selectedWeekGames));
  };

export const loadNFLScheduleFromInboxDB =
  (year: SeasonID): AppThunk =>
  (dispatch, getState) => {
    if (getState().user.authState !== UserAuthState.LoggedIn) return;
    const selectedWeekID: GameWeek = getState().nfl.weekSelected || GameWeek.Week1;
    const seasonID = Season[`Season${year}` as keyof typeof Season];
    const msg = `%c loadNFLScheduleFromInboxDB %c %c ${year} %c %c ${seasonID} %c`;
    if (CONSOLE_ON) console.log(msg, Cls.Loc, '', Cls.Info, '', Cls.Info, '');

    if (getState().schedule.storesLoadedState.loadedSchedule[seasonID]) {
      const msg = `${Emojis.rocket} Schedule for %c ${seasonID} %c has already been loaded`;
      if (CONSOLE_ON) console.log(msg, Cls.Info, '');
      const selectedSeasonSchedule = getState().schedule.scheduleByYear[seasonID];
      const selectedWeekSchedule = parseNFLSelectedWeekSchedule(selectedSeasonSchedule, selectedWeekID);
      dispatch(setGamesSchedule(selectedWeekSchedule));
      dispatch(setMatchupsSchedule(selectedWeekSchedule));
      return;
    }

    if (typeof window !== 'undefined' && window.Worker) {
      const webWorkerDbLoader: Worker = getIndexDBLoaderWebWorker(`LoadSchedule-${year}`);

      const updateLoadedYear = () => dispatch(setStoresLoadedScheduleByYearState({ [`${year}`]: true }));
      const processResponse = (jsonString: string) => {
        const gameSchedule: IGame[] = JSON.parse(jsonString) as IGame[];
        const selectedWeekGames = parseNFLSelectedWeekSchedule(gameSchedule, selectedWeekID);
        dispatch(setGamesSchedule(selectedWeekGames));
        dispatch(setMatchupsSchedule(gameSchedule));
        const yearID = Season[`Season${year}` as keyof typeof Season];
        dispatch(setGamesScheduleByYear({ yearID: yearID, games: gameSchedule }));
        updateLoadedYear();
      };
      const shouldFetch = getShouldFetchGameSchedule(getState());
      webWorkerDbLoader.postMessage(getLoadStoreRequestMessage(Stores.GameSchedule, year, shouldFetch));
      webWorkerDbLoader.onmessage = (e: MessageEvent<IWorkerIDBLoaderResponseMessage>) => {
        const { type, payload } = e.data;
        const msg = `${Emojis.mailNew} from ${Emojis.hammers} to %c ${Stores.GameSchedule} %c %c ${type} %c`;
        if (CONSOLE_ON) console.log(msg, Cls.Info, '', Cls.Loc, '');
        switch (type) {
          case 'result-get-store-data':
            processResponse(payload);
            webWorkerDbLoader.terminate();
            break;

          default:
            if (CONSOLE_ON) console.log(`${Emojis.hammers} UNKNOWN message type %c ${type} %c`, ValueCls, '');
        }
      };
    }
  };

export const loadNFLGameResultsFromInboxDB =
  (year: SeasonID): AppThunk =>
  (dispatch, getState) => {
    if (getState().user.authState !== UserAuthState.LoggedIn) return;

    if (typeof window !== 'undefined' && window.Worker) {
      const webWorkerDbLoader: Worker = getIndexDBLoaderWebWorker(`LoadGameStats-${year}`);

      const processResponse = (jsonString: string) => {
        const gameStats: IGameStats[] = JSON.parse(jsonString) as IGameStats[];
        dispatch(setGameResults(gameStats));
      };
      const shouldFetch = getShouldFetchGameStats(getState());
      webWorkerDbLoader.postMessage(getLoadStoreRequestMessage(Stores.GameStats, year, shouldFetch));
      webWorkerDbLoader.onmessage = (e: MessageEvent<IWorkerIDBLoaderResponseMessage>) => {
        const { type, payload } = e.data;
        const msg = `${Emojis.mailNew} from ${Emojis.hammers} to %c ${Stores.GameStats} %c %c ${type} %c`;
        if (CONSOLE_ON) console.log(msg, Cls.Info, '', Cls.Loc, '');
        switch (type) {
          case 'result-get-store-data':
            processResponse(payload);
            webWorkerDbLoader.terminate();
            break;

          default:
            if (CONSOLE_ON) console.log(`${Emojis.hammers} UNKNOWN message type %c ${type} %c`, ValueCls, '');
        }
      };
    }
  };

export const loadNFLTeamResultsFromInboxDB =
  (year: SeasonID): AppThunk =>
  (dispatch, getState) => {
    if (getState().user.authState !== UserAuthState.LoggedIn) return;

    if (typeof window !== 'undefined' && window.Worker) {
      const webWorkerDbLoader: Worker = getIndexDBLoaderWebWorker(`LoadTeamStats-${year}`);

      const processResponse = (jsonString: string) => {
        const teamStats: IEnhancedTeamStats[] = JSON.parse(jsonString) as IEnhancedTeamStats[];
        dispatch(setTeamStats(teamStats));
      };
      const shouldFetch = getShouldFetchTeamGameStats(getState());
      webWorkerDbLoader.postMessage(getLoadStoreRequestMessage(Stores.TeamWeeklyStats, year, shouldFetch));
      webWorkerDbLoader.onmessage = (e: MessageEvent<IWorkerIDBLoaderResponseMessage>) => {
        const { type, payload } = e.data;
        const msg = `${Emojis.mailNew} from ${Emojis.hammers} to %c ${Stores.TeamWeeklyStats} %c %c ${type} %c`;
        if (CONSOLE_ON) console.log(msg, Cls.Info, '', Cls.Loc, '');
        switch (type) {
          case 'result-get-store-data':
            processResponse(payload);
            webWorkerDbLoader.terminate();
            break;

          default:
            if (CONSOLE_ON) console.log(`${Emojis.hammers} UNKNOWN message type %c ${type} %c`, ValueCls, '');
        }
      };
    }
  };
// #endregion

// #region ADDERS TO IDB ****************************************************************
export const addNFLScheduleToInboxDB =
  (games: IGame[]): AppThunk =>
  (dispatch) => {
    const addedScheduleCb = (gameSchedule: IGame[] | null) => {
      if (gameSchedule) dispatch(addNewGamesToSchedule(gameSchedule));
    };
    updateRecordsInIDBDatabase(Stores.GameSchedule, games, addedScheduleCb);
  };

export const addNFLGameStatsToInboxDB =
  (gameStats: IGameStats[]): AppThunk =>
  (dispatch) => {
    const addedGameStatsCb = (gameStats: IGameStats[] | null) => {
      if (gameStats) dispatch(addNewGameResults(gameStats));
    };
    updateRecordsInIDBDatabase(Stores.GameStats, gameStats, addedGameStatsCb);
  };

export const addNFLTeamStatsToInboxDB =
  (teamStats: IEnhancedTeamStats[]): AppThunk =>
  (dispatch) => {
    const addedTeamStatsCb = (teamStats: IEnhancedTeamStats[] | null) => {
      if (teamStats) dispatch(addTeamStats(teamStats));
    };
    updateRecordsInIDBDatabase(Stores.TeamWeeklyStats, teamStats, addedTeamStatsCb);
  };

// export const addNFLTeamsOnByeWeekToInboxDB =
//   (teams: ITeamsOnByeWeek[]): AppThunk =>
//   (dispatch) => {
//     const addedTeamsOnByeWeekCb = (byeWeekTeams: ITeamsOnByeWeek[] | null) => {
//       if (byeWeekTeams) dispatch(updateByeWeekTeams(byeWeekTeams));
//     };
//     updateRecordsInIDBDatabase(Stores.TeamsOnByeWeek, teams, addedTeamsOnByeWeekCb);
//   };
// #endregion

// #endregion

export const gameSchedule = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    setReloadScheduleFlag: (state, action: PayloadAction<boolean>) => {
      state.reloadScheduleFlag = action.payload;
    },
    // Game Schedule - games, teams, time, etc.
    setGamesSchedule: (state, action: PayloadAction<GameSchedulePayload>) => {
      const dedupe = Array.from(new Set([...action.payload]));
      state.games = dedupe;
      state.storesLoadedState.scheduleLoaded = true;
    },
    setGamesScheduleByYear: (state, action: PayloadAction<GameScheduleByYearPayload>) => {
      const { yearID: seasonYear, games } = action.payload;
      const dedupe = Array.from(new Set([...games]));
      state.scheduleByYear[seasonYear] = dedupe;
      state.storesLoadedState.loadedSchedule[seasonYear] = true;
    },
    updateGamesScheduleByYear: (state, action: PayloadAction<GameScheduleByYearPayload>) => {
      const { yearID: seasonYear, games } = action.payload;
      const dedupe = Array.from(new Set([...state.scheduleByYear[seasonYear], ...games]));
      state.scheduleByYear[seasonYear] = dedupe;
    },
    addNewGamesToSchedule: (state, action: PayloadAction<GameSchedulePayload>) => {
      const dedupe = Array.from(new Set([...state.games, ...action.payload]));
      state.games = dedupe;
      state.storesLoadedState.scheduleLoaded = true;
    },
    clearSchedule: (state) => {
      state.games = [];
      state.storesLoadedState.scheduleLoaded = false;
    },
    // Game Schedule - games, teams, time, etc.
    setMatchupsSchedule: (state, action: PayloadAction<MatchupsSchedulePayload>) => {
      const dedupe = Array.from(new Set([...action.payload]));
      state.matchups = dedupe;
      state.storesLoadedState.matchupsLoaded = true;
    },
    addNewMatchesToSchedule: (state, action: PayloadAction<MatchupsSchedulePayload>) => {
      const dedupe = Array.from(new Set([...state.matchups, ...action.payload]));
      state.matchups = dedupe;
      state.storesLoadedState.matchupsLoaded = true;
    },
    updateMatchesWithPredictions: (state, action: PayloadAction<PredictionsForMatchupsPayload>) => {
      const { predictions, yearID } = action.payload;

      // O(n): Single pass with O(1) lookups
      state.scheduleByYear[yearID as Season] = state.scheduleByYear[yearID as Season].map((game) => {
        const prediction = predictions[game.gameID];
        if (prediction) {
          return {
            ...game,
            predictions: { ...game.predictions, [prediction.modelVersion]: prediction },
          };
        }
        return game;
      });
    },
    clearMatchupsSchedule: (state) => {
      state.matchups = [];
      state.storesLoadedState.matchupsLoaded = false;
    },
    setGameResultsForSeason: (state, action: PayloadAction<{ seasonID: SeasonID; results: IGameStats[] }>) => {
      const { seasonID, results } = action.payload;
      state.gameResults = [
        ...state.gameResults.filter((game) => getSeasonIdFromGameID(game.gameID) !== seasonID),
        ...results,
      ];
      state.storesLoadedState.gameResultsLoaded = true;
    },
    setTeamResultsForSeason: (state, action: PayloadAction<{ seasonID: SeasonID; results: IEnhancedTeamStats[] }>) => {
      const { seasonID, results } = action.payload;
      state.teamResults = [
        ...state.teamResults.filter((team) => !isSeasonYearMatchUID(team.uID, seasonID)),
        ...results,
      ];
      state.storesLoadedState.teamResultsLoaded = true;
    },
    // Game Results - scores, stats, etc.
    setGameResults: (state, action: PayloadAction<GameResultsPayload>) => {
      const dedupe = Array.from(new Set([...action.payload]));
      state.gameResults = dedupe;
      state.storesLoadedState.gameResultsLoaded = true;
    },
    addNewGameResults: (state, action: PayloadAction<GameResultsPayload>) => {
      const dedupe = Array.from(new Set([...state.gameResults, ...action.payload]));
      state.gameResults = dedupe;
      state.storesLoadedState.gameResultsLoaded = true;
    },
    // Team Stats - broken up stats from the game for each team
    setTeamStats: (state, action: PayloadAction<TeamResultPayload>) => {
      const dedupe = Array.from(new Set([...action.payload]));
      state.teamResults = dedupe;
      state.storesLoadedState.teamResultsLoaded = true;
    },
    addTeamStats: (state, action: PayloadAction<TeamResultPayload>) => {
      const dedupe = Array.from(new Set([...state.teamResults, ...action.payload]));
      state.teamResults = dedupe;
      state.storesLoadedState.teamResultsLoaded = true;
    },
    addGameIDToQueue: (state, action: PayloadAction<GameID>) => {
      state.gamesFetchingQueue.push(action.payload);
    },
    removeGameIDFromQueue: (state, action: PayloadAction<GameID>) => {
      state.gamesFetchingQueue = state.gamesFetchingQueue.filter((id) => id !== action.payload);
    },
    setStoresLoadedState: (state, action: PayloadAction<Partial<StoresLoadedState>>) => {
      state.storesLoadedState = { ...state.storesLoadedState, ...action.payload };
    },
    setStoresLoadedScheduleByYearState: (state, action: PayloadAction<Partial<LoadedScheduleByYear>>) => {
      state.storesLoadedState = {
        ...state.storesLoadedState,
        loadedSchedule: {
          ...state.storesLoadedState.loadedSchedule,
          ...action.payload,
        },
      };
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    resetScheduleStore: () => {
      return { ...initialState };
    },
  },
});

export const {
  setReloadScheduleFlag,
  setGamesSchedule,
  updateGamesScheduleByYear,
  addNewGamesToSchedule,
  updateMatchesWithPredictions,
  clearSchedule,
  setMatchupsSchedule,
  addNewMatchesToSchedule,
  clearMatchupsSchedule,
  setGameResults,
  setGameResultsForSeason,
  setTeamResultsForSeason,
  addNewGameResults,
  setTeamStats,
  addTeamStats,
  addGameIDToQueue,
  removeGameIDFromQueue,
  setStoresLoadedState,
  setStoresLoadedScheduleByYearState,
  setGamesScheduleByYear,
  setIsLoading,
  resetScheduleStore,
} = gameSchedule.actions;

// #region UI Selectors *********************************************************
export const selectReloadScheduleFlag = (state: RootState): boolean => state.schedule.reloadScheduleFlag;
export const getSchedule = (state: RootState): (IGame | IMatchup)[] => state.schedule.games;
export const getMatchupSchedule = (state: RootState): IMatchup[] => state.schedule.matchups;
export const getGameResults = (state: RootState): IGameStats[] => state.schedule.gameResults;
export const getTeamResults = (state: RootState): IEnhancedTeamStats[] => state.schedule.teamResults;
export const getFullSchedule = (state: RootState): ScheduleByYear => state.schedule.scheduleByYear;
// export const getByeWeekTeams = (state: RootState): ITeamsOnByeWeek[] => state.schedule.byeWeekTeams;

export const selectScheduleByYearAndWeek = (state: RootState, yearID: SeasonID, weekID: GameWeek): GameID[] => {
  const schedule = getSchedule(state);
  return schedule.filter((s) => s.season === yearID && s.gameWeek.replace(' ', '') === weekID).map((s) => s.gameID);
};

export const getScheduledGamesBySeasonWeekID = createSelector(
  [(state: RootState) => state.schedule.games, (_state: RootState, seasonWeekID: SeasonWeekID) => seasonWeekID],
  (games, seasonWeekID): (IGame | IMatchup)[] => {
    const seasonWeekGames = games.filter((game) => game.seasonWeekID === seasonWeekID);
    return seasonWeekGames.sort(sortWeeklyGamesByDateTime);
  },
);

export const getScheduleByYear = (state: RootState, year: SeasonID): (IGame | IMatchup)[] => {
  const yearID = Season[`Season${year}` as keyof typeof Season];
  return state.schedule.scheduleByYear[yearID];
};

export const isLoadedScheduleByYear = (state: RootState, year: SeasonID): boolean => {
  const yearID = Season[`Season${year}` as keyof typeof Season];
  const seasonSchedule = state.schedule.scheduleByYear[yearID];
  return !!seasonSchedule && seasonSchedule.length === NFL_GAMES_PER_SEASON;
};

export const selectedGameIDsBySeason = (state: RootState, yearID: SeasonID): SeasonalGameIDMap => {
  const yearlyGames = getScheduleByYear(state, yearID);
  const weeklyGameIDsMap: SeasonalGameIDMap = {};

  yearlyGames.forEach((game: IGame | IMatchup) => {
    // const weekID = game.gameWeek.replace(' ', '');
    // const seasonWeekID = toSeasonWeekID(yearID, weekID);
    const seasonWeekID = game.seasonWeekID;
    if (!weeklyGameIDsMap[seasonWeekID]) {
      weeklyGameIDsMap[seasonWeekID] = [];
    }
    weeklyGameIDsMap[seasonWeekID].push(game.gameID);
  });

  return weeklyGameIDsMap;
};

// export const getScheduleForYear2025 = memoize((state: RootState) => {
//   const games = getSchedule(state);
//   return games.filter((game) => game.season === Season.Season2025);
// });

// export const getScheduleForYear2024 = memoize((state: RootState) => {
//   const games = getSchedule(state);
//   return games.filter((game) => game.season === Season.Season2024);
// });

export const getGameInfoByGameID = createSelector(
  [(state: RootState) => state.schedule.scheduleByYear, (_state: RootState, gameID: GameID) => gameID],
  (scheduleByYear, gameID): Maybe<IGame | IMatchup> => {
    const seasonWeekID = getSeasonWeekIDByGameID(gameID);
    const { yearID } = breakSeasonWeekID(seasonWeekID);
    return scheduleByYear[yearID as Season]?.find((game) => game.gameID === gameID);
  },
);

export const getScheduledGamesByWeek = createSelector(
  [getSchedule, getSelectedYear, (_state: RootState, weekID: GameWeek) => weekID],
  (games, seasonID, weekID) => {
    return games.filter(
      (game: IGame | IMatchup) => game.gameWeek.replace(' ', '') === weekID && game.season === seasonID,
    );
  },
);

export const getSelectedWeekSchedule = (state: RootState): (IGame | IMatchup)[] => {
  const selectedWeek = getSelectedWeek(state);
  const selectedYear = getSelectedYear(state);
  const games = getSchedule(state);
  return games.filter(
    (game: IGame | IMatchup) => game.gameWeek.replace(' ', '') === selectedWeek && game.season === selectedYear,
  );
};
// #endregion

// #region Game Selectors *********************************************************
export const getGameIDFetchingQueue = (state: RootState): GameID[] => state.schedule.gamesFetchingQueue;
export const isFetchingGameID = (state: RootState, gameID: GameID): boolean =>
  state.schedule.gamesFetchingQueue.includes(gameID);
// #endregion

// #region Store Init Selectors *********************************************************
export const isGameScheduleEmpty = (state: RootState): boolean => state.schedule.games.length === 0;
export const isGameResultsEmpty = (state: RootState): boolean => state.schedule.gameResults.length === 0;
export const isTeamResultsEmpty = (state: RootState): boolean => state.schedule.teamResults.length === 0;

// #endregion

// #region LOADER Selectors *************************************************************
export const getStoresLoadedState = (state: RootState): StoresLoadedState => state.schedule.storesLoadedState;
export const getIsScheduleLoaded = (state: RootState): boolean => state.schedule.storesLoadedState.scheduleLoaded;
export const getIsMatchupScheduleLoaded = (state: RootState): boolean =>
  state.schedule.storesLoadedState.matchupsLoaded;
export const getIsGameResultsLoaded = (state: RootState): boolean => state.schedule.storesLoadedState.gameResultsLoaded;
export const getIsTeamResultsLoaded = (state: RootState): boolean => state.schedule.storesLoadedState.teamResultsLoaded;
export const getLoadedScheduleByYear = (state: RootState): LoadedScheduleByYear =>
  state.schedule.storesLoadedState.loadedSchedule;
// #region THUNK Selectors **************************************************************
export const isGameResultAvailable = (state: RootState, gameID: GameID): boolean => {
  const gameResults = getGameResults(state);
  const stats: Maybe<IGameStats> = gameResults.find((result) => result.gameID === gameID);

  if (typeof stats === 'undefined') return false;
  if (stats.gameStatus === 'Completed') return true;

  if (stats.error && stats.error.indexOf("Game hasn't started yet") >= 0) {
    // return `false` when there is an error and the game has finished,
    // return `true` otherwise because we can't load anything new after the error
    const gameDate = getGameDateByGameID(gameID);
    const currentTime = Date.now();
    return !(currentTime > gameDate.getTime());
  }

  return false;
};

export const canFetchGameStats = (state: RootState, gameID: GameID): boolean => {
  const gameInfo = getGameInfoByGameID(state, gameID);
  if (!gameInfo) return false;

  const currentTime = Date.now();
  const gameTimeMS = getDateInMSFromEpoch(parseInt(gameInfo.gameTime_epoch));

  switch (gameInfo.gameStatus) {
    case 'Scheduled':
      return gameTimeMS < currentTime;
    case 'Completed':
    case 'Final':
    case 'Final/OT':
    default:
      return true;
  }
};

export const hasGameStatsByGameID = (state: RootState, gameID: GameID): boolean => {
  const gameStats = getGameResults(state);
  const stats: Maybe<IGameStats> = gameStats.find((game) => game.gameID === gameID);

  if (typeof stats === 'undefined') return false;
  if (stats.gameStatus === 'Completed') return true;

  if (stats.error && stats.error.includes("Game hasn't started yet")) {
    // return `false` when there is an error and the game has finished,
    // return `true` otherwise because we can't load anything new after the error
    const gameDate = getGameDateByGameID(gameID);
    const currentTime = Date.now();
    return !(currentTime > gameDate.getTime());
  }

  return false;
};

export const hasGeneratedGameStatsForEachTeam = (state: RootState, gameID: GameID): boolean => {
  const gameResult = getGameResults(state);
  const game = gameResult.find((team) => team.gameID === gameID);
  return !!game && !!game.teamStats;
};

// export const getGameStatsByGameID = (state: RootState, gameID: GameID): IGameStats => {
//   const gameStats = getGameResults(state);
//   return gameStats.find((game) => game.gameID === gameID) as IGameStats;
// };

export const getGameStatsByGameID = createSelector(
  [getGameResults, (_state: RootState, gameID: GameID) => gameID],
  (gameResults, gameID): IGameStats => {
    return gameResults.find((game) => game.gameID === gameID) as IGameStats;
  },
);

export const getSelectedGameStats = createSelector(
  [getGameResults, getSelectedGame],
  (gameResults, selectedGameID): IGameStats => {
    return gameResults.find((game) => game.gameID === selectedGameID) as IGameStats;
  },
);

export const getLatestSeasonWeekIDWithCompletedGames = (state: RootState): Maybe<SeasonWeekID> => {
  const currentYearID = getSelectedYear(state);
  const currentSeasonSchedule = state.schedule.scheduleByYear[currentYearID as Season];

  const latestCompletedGame = currentSeasonSchedule.findLast((game: IGame | IMatchup) =>
    game.gameStatus.startsWith('Final'),
  );
  return latestCompletedGame?.seasonWeekID;
};
/**
 * @deprecated - was used to check if the game had been played and the stats were available from old Manager
 */
export const hasRecordedAllTeamCategoriesByGameID = (state: RootState, gameID: GameID): boolean => {
  const teamStats = getTeamResults(state);
  const [homeTeamID, awayTeamID] = getTeamIDsFromGameID(gameID);

  const homeTeam = teamStats.find((team) => team.gameID === String(gameID) && team.teamID === homeTeamID);
  const awayTeam = teamStats.find((team) => team.gameID === String(gameID) && team.teamID === awayTeamID);

  return !!(homeTeam && awayTeam);
};

// #endregion

export default gameSchedule.reducer;
